// Kit compartido del módulo Hotel (esquema `hotel`, scripts 915-921).
// Toda escritura es por RPC (las tablas solo tienen SELECT): este archivo
// define el helper, los tipos de cada RPC y etiquetas comunes. Los
// componentes de UI (Campo, Modal, fieldCls, useSort, SortTh, money, num) se
// reutilizan del kit de inventario para que el módulo se vea como el resto.
import type { ReactNode } from 'react';
import { apiClient } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

// El Modal del kit de inventario NO se reexporta: regla del proyecto (2026-10-10),
// sin ventanas modales. En el módulo Hotel «Modal» es un Panel integrado (abajo).
export { Campo, Switch, fieldCls, money, num, useSort, SortTh } from '@/features/inventario/shared';

const HOTEL = { headers: { 'Content-Profile': 'hotel', 'Accept-Profile': 'hotel' } };
export const hotelRpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body, HOTEL)).data;

// Lectura directa de tablas del esquema (RLS por empresa)
export const hotelTabla = async <T,>(tabla: string, query = ''): Promise<T[]> =>
  (await apiClient.get<T[]>(`/${tabla}${query ? `?${query}` : ''}`, HOTEL)).data;

// Prefijo común de queryKeys del módulo (para invalidar todo junto)
export const QK = 'hotel';

export const hoyISO = () => {
  const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};
export const sumarDias = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
export const fmtFecha = (iso: string | null | undefined) =>
  (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(LOCALE, { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const fmtCorta = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(LOCALE, { weekday: 'short', day: '2-digit' });

// ── Estados ────────────────────────────────────────────────────────────────
export type EstadoReserva = 'tentativa' | 'confirmada' | 'en_casa' | 'salida' | 'cancelada' | 'no_show';
export const ESTADO_RESERVA: Record<EstadoReserva, { label: string; cls: string; bar: string }> = {
  tentativa:  { label: 'Tentativa',  cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', bar: 'bg-amber-400' },
  confirmada: { label: 'Confirmada', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',         bar: 'bg-sky-500' },
  en_casa:    { label: 'En casa',    cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300', bar: 'bg-emerald-500' },
  salida:     { label: 'Salida',     cls: 'bg-muted text-muted-foreground', bar: 'bg-slate-400' },
  cancelada:  { label: 'Cancelada',  cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', bar: 'bg-red-300' },
  no_show:    { label: 'No-show',    cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300', bar: 'bg-rose-400' },
};
export const LIMPIEZA: Record<string, { label: string; cls: string }> = {
  sucia:         { label: 'Sucia',         cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  limpia:        { label: 'Limpia',        cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300' },
  inspeccionada: { label: 'Inspeccionada', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
};
export const MANT: Record<string, string> = { ok: 'Operativa', fuera_orden: 'Fuera de orden', fuera_servicio: 'Fuera de servicio' };
export const CANALES = [
  ['recepcion', 'Recepción'], ['telefono', 'Teléfono'], ['directo', 'Web directa'], ['empresa', 'Empresa'], ['ota', 'OTA'], ['ical', 'iCal'],
] as const;

// ── Contratos de las RPC ───────────────────────────────────────────────────
export interface HotelConfig {
  nombre_comercial: string | null; hora_checkin: string; hora_checkout: string; fecha_hotel: string;
  iva_pct: number; iva_feriado_activo: boolean; iva_feriado_pct: number; servicio_pct: number; tasa_turistica: number;
  deposito_pct: number; deposito_horas: number; cancel_dias: number; cancel_penalidad_noches: number; noshow_noches: number;
  overbooking_max: number; limite_credito_default: number | null; cc_contable_id: string | null; centro_costo_id: string | null;
  categoria_factura: string; slug_publico: string | null; activo: boolean; exigir_tct?: boolean; factura_fin_mes?: boolean; early_checkin_pct?: number; late_checkout_pct?: number; exigir_turno_caja?: boolean; cierre_auto?: boolean; cierre_auto_hora?: string; zona_horaria?: string;
}
export interface TipoHabitacion {
  id: string; codigo: string; nombre: string; descripcion: string | null; capacidad_adultos: number; capacidad_ninos: number;
  camas: string | null; tarifa_base: number; imagen: string | null; orden: number; es_activo: boolean;
  ocupacion_base?: number; extra_adulto?: number; extra_nino?: number; ninos_sin_recargo?: number;
}
export interface Habitacion {
  id: string; numero: string; tipo_id: string; piso: string | null; estado_limpieza: string; estado_mant: string;
  notas: string | null; orden: number; es_activo: boolean;
}
export interface Plan {
  id: string; codigo: string; nombre: string; ajuste_tipo: 'pct' | 'monto'; ajuste_valor: number; incluye_desayuno: boolean;
  reembolsable: boolean; deposito_pct: number | null; cancel_dias: number | null; cancel_penalidad_noches: number | null;
  noshow_noches: number | null; partner_id: string | null; es_publico: boolean; orden: number; es_activo: boolean;
}
export interface TarifaDia { tipo_id: string; fecha: string; precio: number; min_estadia: number | null; max_estadia: number | null; cta: boolean; ctd: boolean; cerrada: boolean }

// hotel.cotizar(_tipo, _plan, _llegada, _salida, _adultos, _ninos)
export interface Cotizacion {
  tipo_id: string; plan_id: string | null; llegada: string; salida: string; noches_n: number;
  noches: { fecha: string; precio: number; iva_pct: number; iva: number; componentes?: { nombre: string; tipo: string; cantidad: number; monto: number; iva_pct: number }[] }[];
  alojamiento?: number; incluido_plan?: number;
  subtotal: number; iva: number; servicio: number; tasa: number; total: number; deposito: number;
  disponibles_min: number; disponible: boolean; errores: string[];
  politica: { reembolsable: boolean; cancel_dias: number; cancel_penalidad_noches: number; noshow_noches: number; incluye_desayuno: boolean };
}
// hotel.disponibilidad(_desde, _hasta)
export interface DisponibilidadTipo {
  tipo_id: string; codigo: string; nombre: string;
  dias: { fecha: string; disponibles: number; precio: number; cerrada: boolean }[];
}
// hotel.rack(_desde, _hasta)
export interface RackReserva {
  id: string; codigo: string; estado: EstadoReserva; canal: string; habitacion_id: string | null; tipo_id: string;
  llegada: string; salida: string; adultos: number; ninos: number; huesped: string | null; total: number;
  deposito_requerido: number; deposito_pagado: number; deposito_vence: string | null;
}
export interface Rack {
  fecha_hotel: string;
  habitaciones: { id: string; numero: string; piso: string | null; tipo_id: string; tipo: string; estado_limpieza: string; estado_mant: string }[];
  reservas: RackReserva[];
  bloqueos: { id: string; habitacion_id: string | null; tipo_id: string | null; desde: string; hasta: string; motivo: string | null; origen: string }[];
}
// hotel.reservas_listar(_desde, _hasta, _estado, _busca)
export interface ReservaFila {
  id: string; codigo: string; estado: EstadoReserva; canal: string; llegada: string; salida: string; noches: number;
  adultos: number; ninos: number; huesped: string | null; identificacion: string | null; contacto_telefono: string | null;
  contacto_email: string | null; tipo: string; habitacion: string | null; plan: string | null; total: number;
  deposito_requerido: number; deposito_pagado: number; deposito_vence: string | null; penalidad: number;
  penalidad_liquidada: boolean; created_at: string;
}
// hotel.reserva_obtener(_id) — fila completa de hotel.reservas + anexos
export interface ReservaDetalleData extends Omit<ReservaFila, 'huesped' | 'tipo' | 'habitacion' | 'plan' | 'noches'> {
  tipo_id: string; plan_id: string | null; habitacion_id: string | null; titular_partner_id: string | null;
  facturar_partner_id: string | null; notas: string | null; token: string; motivo_cancelacion: string | null;
  checkin_at: string | null; checkout_at: string | null;
  contacto_nombre: string | null; contacto_telefono: string | null; contacto_email: string | null;
  hora_llegada: string | null; llegada_garantizada: boolean;
  movimientos?: { fecha: string; de: string | null; a: string; modo: string; diferencia: number; motivo: string }[];
  perfil?: { vip: boolean; preferencias: string | null; alergias: string | null; notas: string | null; lista_negra: boolean; motivo_lista: string | null; estadias?: number } | null;
  trazas?: { id: string; tipo: 'traza' | 'recado'; departamento: string; texto: string; fecha: string; resuelta: boolean }[];
  huesped: { id: string; nombre: string; identificacion: string; email: string | null; telefono: string | null } | null;
  empresa_facturar: { id: string; nombre: string; identificacion: string } | null;
  tipo: { codigo: string; nombre: string }; habitacion: string | null; plan: string | null;
  noches: { fecha: string; precio: number; iva_pct: number }[];
  registro: { nombres: string; tipo_documento: string; documento: string; nacionalidad: string | null; es_titular: boolean }[];
  folio_id: string | null;
}
// hotel.folio_obtener(_reserva)
export interface FolioCargo { id: string; fecha: string; tipo: string; descripcion: string; monto_base: number; iva_pct: number; iva_monto: number; total: number; anulado: boolean; anulado_motivo: string | null; facturado?: boolean }
export interface FolioPago { id: string; fecha: string; monto: number; forma: string; referencia: string | null; devuelto?: number }
export interface FolioFactura { ticket_id: string; serie: string | null; total: number; saldo: number; estado: string | null; parcial: boolean }
export interface FolioVentana {
  id: string; numero: number; nombre: string; partner_id: string | null; partner: string | null; identificacion: string | null;
  ticket_id: string | null; factura: string | null; saldo: number; cargos: FolioCargo[]; pagos: FolioPago[]; facturas?: FolioFactura[]; rutas?: string[];
}
export interface Folio { folio_id: string; estado: 'abierto' | 'cerrado'; limite_credito: number | null; saldo: number; deposito_disponible?: number; ventanas: FolioVentana[] }
// hotel.checkout(_id, _d) → facturas[]
export interface CheckoutResp {
  ok: boolean; deposito_sobrante: number; vuelto?: number; deposito_devuelto?: number;
  facturas: { ventana: string; ticket_id: string; serie?: string | null; facturas?: number; total: number; deposito_aplicado: number; pagado: number; saldo_credito: number; emision_error: string | null }[];
}
// hotel.housekeeping()
export interface HkHabitacion {
  id: string; numero: string; piso: string | null; tipo: string; estado_limpieza: string; estado_mant: string;
  ocupada: boolean; huesped: string | null; sale_hoy: boolean; llega_hoy: boolean; llegada_codigo: string | null;
  reserva_id?: string | null; no_molestar?: boolean; camarera?: string | null; creditos?: number | null; discrepancia?: boolean;
  // 948: tarea del día (control de camareras)
  llegada_hora?: string | null; vip?: boolean;
  tarea_id?: string | null; camarera_id?: string | null; tarea_tipo?: 'salida' | 'ocupada' | 'libre' | 'profunda' | null;
  tarea_estado?: EstadoTareaHk | null; inicio_at?: string | null; fin_at?: string | null; pausa_motivo?: string | null;
  minutos?: number | null; estandar?: number | null; rapida?: boolean;
  prioridad?: number; prioridad_manual?: boolean; prioridad_motivo?: string | null;
  // 949: calidad
  relimpieza?: number; inspeccion_estado?: 'aprobada' | 'rechazada' | null;
  inspeccion?: 'obligatoria' | 'muestra' | null; inspeccion_motivo?: string | null; faltantes?: number;
}
export type EstadoTareaHk = 'pendiente' | 'en_proceso' | 'pausa' | 'terminada';
export const ESTADO_TAREA_HK: Record<EstadoTareaHk, { label: string; cls: string }> = {
  pendiente:  { label: 'Pendiente',  cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  en_proceso: { label: 'En limpieza', cls: 'bg-blue-600 text-white' },
  pausa:      { label: 'En pausa',   cls: 'bg-amber-500 text-amber-950' },
  terminada:  { label: 'Terminada',  cls: 'bg-emerald-600 text-white' },
};
export const MOTIVO_PAUSA: Record<string, string> = {
  no_molestar: 'No molestar', huesped_dentro: 'Huésped dentro', rechazo: 'Rechazó el servicio', otro: 'Otro motivo',
};
// hotel.hk_camareras_listar()
export interface CamareraHk {
  id: string; nombre: string; empleado_id: string | null; empleado: string | null; tiene_pin: boolean; bloqueada: boolean;
  creditos_max: number | null; tope: number; activo: boolean; aviso_aceptado_at: string | null;
  creditos_hoy: number; tareas_hoy: number; terminadas_hoy: number;
}
// El piso puede venir como «3» o como «Piso 3».
export const etiquetaPiso = (p: string | null | undefined) => (!p ? 'Sin piso' : /^\d/.test(p) ? `Piso ${p}` : p);
// hotel.dashboard()
export interface HotelDashboard {
  fecha_hotel: string; habitaciones: number;
  hoy: { ocupadas: number; ocupacion_pct: number; adr: number; revpar: number } | null;
  llegadas: number; salidas: number; en_casa: number; sucias: number; fuera_servicio: number; pickup_7d: number;
  tentativas_por_vencer: number; por_liquidar: number;
  proximos_30: { fecha: string; ocupadas: number; ocupacion_pct: number }[];
  ultimo_cierre: CierreResumen | null;
}
// hotel.cierre_nocturno(_forzar)
export interface CierreResumen {
  fecha: string; habitaciones: number; ocupadas: number; ocupacion_pct: number; ingreso_hospedaje: number; adr: number; revpar: number;
  servicio: number; tasa: number; no_shows: number; cancelaciones_deposito: number; salidas_forzadas: string | null; nueva_fecha_hotel?: string;
}
// hotel.huespedes_en_casa() — selector del POS
export interface HuespedEnCasa { reserva_id: string; codigo: string; habitacion: string; huesped: string; titular_partner_id: string; salida: string; saldo: number; limite_credito: number | null }

/** Sección en la misma página (reemplaza a las ventanas modales): ancho completo. */
export function Panel({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return <PanelIntegrado title={title} onClose={onClose} wide>{children}</PanelIntegrado>;
}

/** Compatibilidad: las pantallas que usaban <Modal> ahora muestran un Panel
 *  integrado en la página (sin fondo ni ventana encima). */
export function Modal(props: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return <Panel {...props} />;
}

// Preguntas dentro de la pantalla (confirmar / pedir / avisar): componente común.
export { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
